/**
 * #757 — "the requested window is empty, so here is the next one that isn't."
 *
 * One module owns that notion for every board: which slate is shown instead,
 * and how the swap reads on screen. The Moneyline board (#712) and the
 * Spread/Total board reach it from opposite directions — the dashboard derives
 * moneyline rows, while the Spread/Total board derives its own — so the rule
 * lives here rather than in either of them, and the two cannot drift into
 * landing on different days from the same store.
 *
 * What is NOT here: which rows a board is eligible to show. Sport, circuit, CFB
 * subdivision, status, archived/final, and Poly-only lines are each board's own
 * business and are applied by the caller BEFORE the rows arrive — a fallback
 * that picked a date first would land on a day whose only rows the board then
 * filters away, and show an empty board under a banner promising games.
 */

import type { SlateRange } from './types';
import {
  formatSlateRange,
  isoToMdy,
  slateWindow,
  slateWindowContains,
  startOfIsoDayMs,
  type SlateWindow,
} from './weeklySlate';

/** The slate a board fell forward to: the window it lands on, and the rows
 *  inside it, in board order. */
export interface UpcomingSlate<T> {
  window: SlateWindow;
  rows: T[];
}

/**
 * The next slate with rows at or after `fromIso`, or null when there is none.
 *
 * Only the FIRST day with rows is taken, then widened to the board's window —
 * so a dense daily sport shows one day's slate rather than a pile of every
 * future day, and a sparse one still isn't a dead end when its next game is two
 * months out. Week mode falls forward a whole window rather than a day (#712):
 * the rows on screen have to be the same shape as the view they stand in for,
 * or everything around them would say week over a single day's rows.
 *
 * `sortMs` decides what "at or after" means (an event's start instant, falling
 * back to its own midnight), and orders the result; `tieBreak` settles rows that
 * start together.
 *
 * CALLERS OWE IT DATEABLE ROWS. A row whose `dateOf` is not a real calendar
 * date wins the earliest-date comparison — `''` sorts before every date — and
 * takes the slate over, leaving a banner that names no day. Both of today's
 * callers are safe by construction (a Kalshi per-day ticker always parses a
 * date, and the store falls back to it when the feed stamps no start time), so
 * this is a constraint to meet at adoption rather than a live defect; the
 * dashboard's multi-outcome fallback, whose events genuinely can lack a date,
 * already excludes them before it picks. Anyone widening this helper to a feed
 * like that one must filter first — and should make it the helper's own rule
 * at that point, which is a deliberate change to the moneyline board's
 * behavior and belongs in its own commit.
 */
export function pickUpcomingSlate<T>(params: {
  rows: readonly T[];
  /** First day of the empty window the trader asked for. */
  fromIso: string;
  range: SlateRange;
  dateOf: (row: T) => string;
  sortMs: (row: T) => number;
  tieBreak: (row: T) => string;
}): UpcomingSlate<T> | null {
  const { rows, fromIso, range, dateOf, sortMs, tieBreak } = params;
  const dayStartMs = startOfIsoDayMs(fromIso);
  if (!Number.isFinite(dayStartMs)) return null;
  // The requested window is empty (the caller checked), so anything at or after
  // its first day necessarily falls after it — in either mode.
  const upcoming = rows.filter((row) => sortMs(row) >= dayStartMs);
  if (upcoming.length === 0) return null;
  const nextDate = upcoming.reduce(
    (soonest, row) => (dateOf(row) < soonest ? dateOf(row) : soonest),
    dateOf(upcoming[0]),
  );
  const window = slateWindow(nextDate, range);
  const landed = upcoming
    .filter((row) => slateWindowContains(window, dateOf(row)))
    .sort((a, b) => {
      const startCmp = sortMs(a) - sortMs(b);
      if (startCmp !== 0) return startCmp;
      return tieBreak(a).localeCompare(tieBreak(b));
    });
  return landed.length > 0 ? { window, rows: landed } : null;
}

/**
 * The banner's second sentence — what the board swapped in.
 *
 * Named in the same unit the board is showing: without that a weekly slate
 * would announce "the next day with games" over a week's worth of rows. `noun`
 * is what the board's rows ARE ("games" on Moneyline, "lines" on
 * Spread/Total), so the sentence stays true of what is on screen.
 */
export function upcomingSlateCopy(
  window: SlateWindow | null,
  range: SlateRange,
  noun: string,
): string {
  const unit = range === 'week' ? 'week' : 'day';
  if (!window) return `Showing the next ${unit} with ${noun}.`;
  const landed = range === 'week' ? formatSlateRange(window) : isoToMdy(window.startIso);
  return landed
    ? `Showing the next ${unit} with ${noun} — ${landed}.`
    : `Showing the next ${unit} with ${noun}.`;
}

/**
 * The banner's first sentence — the window the trader actually asked for,
 * named: `on 09/01/2026`, or `in Aug 31 – Sep 6` in Week mode.
 *
 * A week whose label can't be built degrades to naming its first day rather
 * than rendering a broken range, matching what `formatSlateRange` promises its
 * callers.
 */
export function slateScopeCopy(window: SlateWindow, range: SlateRange): string {
  const label = range === 'week' ? formatSlateRange(window) : null;
  return label ? `in ${label}` : `on ${isoToMdy(window.startIso)}`;
}
